<template>
    <b-modal id="create_webhook_modal" @ok="createWebhook">
        <template slot="modal-title">Create a new webhook</template>
        <form ref="create_webhook_form">
            <b-form-group
                    label="Name"
                    label-for="name">
                <b-form-input
                        id="name"
                        v-model="new_webhook.name"
                        :state="validateName"
                        autofocus
                        required/>
                <b-form-invalid-feedback :state="validateName">
                    Name is invalid, it needs at least {{minNameLength}} characters.
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
                    label="Project"
                    label-for="project">
                <b-form-select
                        id="project"
                        :state="validateProject"
                        v-model="new_webhook.project_id"
                        :options="project_options"
                        required/>
                <b-form-invalid-feedback :state="validateProject">
                    Select a project.
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
                    label="Event"
                    label-for="event">
                <b-form-select
                        id="event"
                        :state="validateEvent"
                        v-model="new_webhook.event_name"
                        :options="event_options"
                        required/>
                <b-form-invalid-feedback :state="validateEvent">
                    Select an event.
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
                    label="Make a path for the webhook url"
                    label-for="path">
                <b-form-input
                        id="path"
                        v-model="new_webhook.path"
                        :state="validatePath"
                        required/>
                <b-form-invalid-feedback :state="validatePath">
                    A path is required and needs to be at least {{minPathLength}} characters long.
                </b-form-invalid-feedback>
                <div class="strike">
                    <span>Or generate a random path</span>
                </div>
                <b-form-group>
                    <b-form-input
                            id="pathLength"
                            type="range"
                            :min="minPathLength"
                            :color="sitionColor"
                            max="100"
                            v-model="pathLength"
                            oninput="amount.value=this.pathLength"/>
                    <div align="center">
                        <b-btn class="btn btn-primary btn-sm" v-on:click="generateRandomPath">
                            Generate a: {{this.pathLength}} character random path
                        </b-btn>
                    </div>
                </b-form-group>
            </b-form-group>
            <b-form-group
                    label="Notes and description (optional)"
                    label-for="notes">
                <b-form-textarea
                        id="notes"
                        v-model="new_webhook.notes_and_description"/>
            </b-form-group>
        </form>
    </b-modal>
</template>

<script>
    import WebhookRepository from '../../repositories/webhook.repository';

    export default {
        props: {
            project_options: {
                type: Array,
                default: () => []
            },
            event_options: {
                type: Array,
                default: () => []
            },
            pathLength: {
                type: Number,
                default: parseInt(process.env.VUE_APP_WEBHOOK_DEFAULT_PATH_LENGTH)
            },
        },
        data() {
            return {
                new_webhook: WebhookRepository.blankWebhook(),
                minNameLength: process.env.VUE_APP_VALIDATION_WEBHOOK_MIN_NAME_LENGTH,
                minPathLength: process.env.VUE_APP_VALIDATION_WEBHOOK_MIN_PATH_LENGTH,
            }
        },
        methods: {
            generateRandomPath() {
                this.new_webhook.path = WebhookRepository.generateRandomPath(this.pathLength);
            },
            checkFields() {
                if (WebhookRepository.validate.name(this.new_webhook.name) === false) return false;
                if (WebhookRepository.validate.project(this.new_webhook.project_id) === false) return false;
                if (WebhookRepository.validate.event(this.new_webhook.event_name) === false) return false;
                if (WebhookRepository.validate.path(this.new_webhook.path) === false) return false;
                return true;
            },
            createWebhook(modal) {
                if (this.$refs.create_webhook_form.checkValidity() && this.checkFields()) {
                    this.$emit('change-create-loading');
                    WebhookRepository.post(this.new_webhook).then(res => {
                        this.$emit('change-create-loading');
                        if (res.status === 201) {
                            // Emit the webhook created event
                            this.$emit('webhook-created', res.data);
                            // Notify the user.
                            this.$bvToast.toast(`Webhook ${res.data.name} successfully created.`, {
                                title: `Webhook created.`,
                                variant: 'success',
                                solid: true
                            });
                            this.new_webhook = WebhookRepository.blankWebhook();
                        } else {
                            this.$bvToast.toast('Looks like something went wrong while creating a new webhook.', {
                                title: `Webhook creation error.`,
                                variant: 'danger',
                                solid: true
                            });
                        }
                    }).catch(error => {
                        this.$emit('change-create-loading');
                        this.$bvModal.show('create_webhook_modal');
                        if (error.response.status === 422) {
                            console.error("Errorcode: " + error.response.status + " Type: " + error.response.statusText + " \n details:" + error.response.data['hydra:description']);
                            this.$bvToast.toast('Looks like something went wrong while validating the new webhook. Check the value\'s of all the fields', {
                                title: `Webhook validation error.`,
                                variant: 'danger',
                                solid: true
                            });
                        } else if (error.response.status === 409) {
                            console.error("Errorcode: " + error.response.status + " Type: " + error.response.statusText + " \n details:" + error.response.data['hydra:description']);
                            this.$bvToast.toast('Looks like something went wrong while creating the webhook. Pls. check if the path of the url unique is in the project scope.', {
                                title: `Webhook unique error.`,
                                variant: 'danger',
                                solid: true
                            });
                        } else {
                            console.error("Errorcode: " + error.response.status + " Type: " + error.response.statusText);
                            this.$bvToast.toast('Looks like something went wrong on the server while creating a new webhook.', {
                                title: `Webhook creation error.`,
                                variant: 'danger',
                                solid: true
                            });
                        }
                    });
                } else {
                    modal.preventDefault();
                }
            }
        },
        computed: {
            validateProject() {
                return WebhookRepository.validate.project(this.new_webhook.project_id);
            },
            validateName() {
                return WebhookRepository.validate.name(this.new_webhook.name);
            },
            validateEvent() {
                return WebhookRepository.validate.event(this.new_webhook.event_name);
            },
            validatePath() {
                return WebhookRepository.validate.path(this.new_webhook.path);
            },
        }
    }
</script>

<style>
    .strike {
        display: block;
        text-align: center;
        overflow: hidden;
        white-space: nowrap;
        font-size: 15px;
        color: grey;
    }

    .strike > span {
        position: relative;
        display: inline-block;
    }

    .strike > span:before,
    .strike > span:after {
        content: "";
        position: absolute;
        top: 50%;
        width: 9999px;
        height: 1px;
        background: lightgray;
    }

    .strike > span:before {
        right: 100%;
        margin-right: 10px;
    }

    .strike > span:after {
        left: 100%;
        margin-left: 10px;
    }

</style>
